import { Component, OnInit, OnDestroy, Input, HostBinding, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { NotificationStateAction } from '@app/shared/models';
import { CustomToastersService } from 'src/app/shared/services';
import { NavNotificationsService } from '@app/api/api-services/nav-notifications.service';
import { NotificationItemApiModel } from '@app/api/api-models/notifications/NotificationItemApiModel';
import { GetNotificationsModel } from '@app/api/api-models/notifications/GetNotificationsModel';
import { CustomUtils } from '@app/shared/utils/custom-utils';

@Component({
  selector: 'a360-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NotificationComponent implements OnInit, OnDestroy {
  @Input()
  @HostBinding('class.open')
  public isNotificationsOpen = false;

  @Input()
  public isPlatformTopNavEnabled = false;

  @HostBinding('style.filter')
  public filterStyle: string | null = null;

  public totalCount = 0;
  public pending = false;
  public isNextPageLoading = false;
  public isFullListDownloaded = false;
  public collection: NotificationItemApiModel[] = [];

  private readonly pageSize = 50;
  private readonly maxPercentOfPassedScrollDistance = 80;
  private readonly subscriptions = new Array<Subscription>();

  constructor(
    private readonly customToastersService: CustomToastersService,
    private notificationsService: NavNotificationsService
  ) {}

  public ngOnInit(): void {
    let subscription = this.notificationsService
      .subscribeNotificationsStateObserver()
      .subscribe((isNotificationsOpen) => {
        this.isNotificationsOpen = isNotificationsOpen;
        this.isNotificationsOpen ? this.loadNotifications() : this.resetNotifications();

        if (this.isNotificationsOpen) {
          const mainElement = document.querySelector('main');
          if (mainElement?.classList.contains('night-mode')) {
            this.filterStyle = 'invert(1)';
          } else {
            this.filterStyle = null;
          }
        }
      });
    this.subscriptions.push(subscription);

    subscription = this.notificationsService.subscribeCounterObserver().subscribe((totalCount) => {
      this.totalCount = totalCount;
    });
    this.subscriptions.push(subscription);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  public onScroll(event: any): void {
    if (this.isNextPageLoading || this.collection.length >= this.totalCount) {
      return;
    }
    const table = event.srcElement;
    if (CustomUtils.isEndOfElemReached(table, this.maxPercentOfPassedScrollDistance)) {
      this.isNextPageLoading = true;
      this.loadNotifications();
    }
  }

  public close(): void {
    this.isNotificationsOpen = false;
    this.notificationsService.emitNotificationsStateObserver(this.isNotificationsOpen);
  }

  public resetNotifications(): void {
    this.collection = [];
  }

  public markAll(): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.customToastersService.waitProcessing();

    const subscription = this.notificationsService.markAllAsRead().subscribe(
      () => {
        const model = new NotificationStateAction();
        model.isAllRead = true;
        this.collection.forEach((x) => (x.IsRead = true));
        this.notificationsService.emitNotificationStateActionObserver(model);
        this.pending = false;
        this.customToastersService.completeProcessing();
      },
      () => {
        this.handleError();
        this.customToastersService.failProcessing();
      }
    );

    this.subscriptions.push(subscription);
  }

  public deleteAll(): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.customToastersService.waitRemove();

    const subscription = this.notificationsService.deleteAll().subscribe(
      () => {
        const model = new NotificationStateAction();
        model.isAllRead = true;
        this.notificationsService.emitNotificationStateActionObserver(model);
        this.resetNotifications();
        this.pending = false;
        this.customToastersService.completeRemove();
      },
      () => {
        this.handleError();
        this.customToastersService.failRemove();
      }
    );

    this.subscriptions.push(subscription);
  }

  private loadNotifications(): void {
    if (this.pending) {
      return;
    }

    if (!this.isNextPageLoading) {
      this.pending = true;
    }

    const model = new GetNotificationsModel(this.collection.length, this.pageSize);

    const subscription = this.notificationsService.get(model).subscribe(
      (notifications: NotificationItemApiModel[]) => {
        if (this.collection.length > 0) {
          this.collection = this.collection.concat(notifications);
        } else {
          this.collection = notifications;
        }
        this.collection = _.orderBy(this.collection, (x) => x.IsRead);
        this.pending = false;
        this.isNextPageLoading = false;
      },
      () => {
        this.handleError();
        this.customToastersService.failLoad();
      }
    );

    this.subscriptions.push(subscription);
  }

  public handleRemovedNotification(notification: NotificationItemApiModel): void {
    this.collection = this.collection.filter((x) => x.Id !== notification.Id);
  }

  private handleError(): void {
    this.pending = false;
  }
}
